import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import TreeNode from './components/TreeNode/TreeNode';
import Utils from './../../../../utils';
import TreeNodeType from './constants/TreeNodeType';
import Truncation from './constants/Truncation';

/**
 * TreeView component
 *
 * @example
 * <TreeView
 *     nodes={ {
 *         'sample/node/path': {
 *             path: 'sample/node/path',
 *             type: TreeNodeType.FOLDER,
 *             isRoot: true,
 *             children: [ 'sample/node/path/item' ]
 *         }
 *         'sample/node/path/item': {
 *             path: 'sample/node/path/item',
 *             type: TreeNodeType.FILE,
 *             isRoot: false,
 *             children: []
 *         }
 *     } }
 *     onToggle={ onToggle },
 *     truncation:Truncation.ELLIPSIS,
 *     icons = {
 *          indicatorIcon: 'caret-right',
 *          indicatorIconOpen: 'caret-down',
 *          nodeIcon: {
 *              folder: 'folder-open',
 *              file: 'file'
 *          }
 *      },
 *      classes: {
 *          treeNode: {}
 *      },
 *      selectedNodeActionComponent: someFunction(node) { console.log(node); },
 *      useNameInsteadOfKey: true,
 *      showDeactivated: true
 * />
 */
export default class TreeView extends Component {
    getRootNodes() {
        const { nodes } = this.props;
        const rootNodes = [];

        for (const keyPath in nodes) {
            if (nodes[keyPath]) {
                if (nodes[keyPath].isRoot) {
                    rootNodes.push(nodes[keyPath]);
                }
            }
        }

        return rootNodes;
    }

    getChildNodes(node) {
        const { nodes } = this.props;

        if (!node.children) {
            return [];
        }

        return node.children.map(path => nodes[path]);
    }

    render() {
        const {
            nodes,
            truncation,
            onToggle,
            icons,
            classes,
            selectedNodeActionComponent,
            useNameInsteadOfKey,
            showDeactivated
        } = this.props;

        if (Utils.isEmpty(nodes)) {
            return null;
        }

        const rootNodes = this.getRootNodes();

        return (
            <Fragment>
                { rootNodes.map((node, index) => (
                    <TreeNode
                        key={ `treeNode-${index}` }
                        node={ node }
                        getChildNodes={ this.getChildNodes.bind(this) }
                        onToggle={ onToggle }
                        truncation={ truncation }
                        icons={ icons }
                        classes={ classes }
                        selectedNodeActionComponent={ selectedNodeActionComponent }
                        useNameInsteadOfKey={ useNameInsteadOfKey }
                        showDeactivated={ showDeactivated }
                    />
                ))}
            </Fragment>
        );
    }
}

TreeView.propTypes = {
    nodes: PropTypes.shape({
        path: PropTypes.string,
        type: PropTypes.oneOf([TreeNodeType.FOLDER, TreeNodeType.FILE]),
        isRoot: PropTypes.bool,
        children: PropTypes.array
    }),
    onToggle: PropTypes.func.isRequired,
    truncation: PropTypes.oneOf([Truncation.ELLIPSIS, Truncation.BREAK_WORD]),
    icons: PropTypes.object,
    classes: PropTypes.object,
    selectedNodeActionComponent: PropTypes.func,
    showDeactivated: PropTypes.bool
};

TreeView.defaultProps = {
    icons: {},
    classes: {
        treeNode: ''
    },
    showDeactivated: false
};
